import { useContext, useMemo } from 'react';
import {
  TEDLRFakturaAttestAttestTypEnum,
  TEDLRFakturaAttestSTATUSEnum,
  TEDLRFakturaAttestTYPEnum,
  TEDLRFakturaDelAttestMinaStatusEnum,
} from '../../../../../../globalQueryTypes';
import {
  IValidateFn,
  mergeBusinessDataFormValues,
  parseText,
  IFieldValidations,
  createFormData,
} from '../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import {
  FakturaDelQuery_node_TEDLRFakturaDel,
  FakturaDelQuery_node_TEDLRFakturaDel__Associations_Faktura__Associations_Fakturadel__Associations_Fakturaattest,
} from '../Queries/_QueryTypes/FakturaDelQuery';
import { LRSysOptionsQuery_db_SYSOptions_All } from '../Queries/LRSysOptionQuery/_QueryTypes/LRSysOptionsQuery';
import { LoginStateContext } from '../../../../../Login/Contexts/LoginStateContext/LoginStateContext';
import { FakturaattestWrapperContext } from '../../../Rutiner/FakturaDel/Contexts/FakturaattestWrapperContext';
import { getAktuellFakturaAttest } from '../Utils/FakturadelUtils';
import KonteringContext, {
  IAttestKodbehorighetUrval,
} from '../../../../_Common/Components/KonteringManager/KonteringContext';
import {
  FakturaDelType,
  FakturaDelType__Associations_Fakturaattest_All,
} from '../Queries/_QueryTypes/FakturaDelType';

const containsOtherAttestant = (
  attestantSign: string,
  attestanter: FakturaDelQuery_node_TEDLRFakturaDel__Associations_Faktura__Associations_Fakturadel__Associations_Fakturaattest[]
) => attestanter.some(attestant => attestantSign !== attestant.AttestantSign);

export const validateFinnsBAOnFakturaDel = (
  fakturaAttester: FakturaDelType__Associations_Fakturaattest_All[]
): IFieldValidations => {
  const result: IFieldValidations = [];
  const finnsBeslutsattestant = fakturaAttester?.some(
    (fa: any) => fa.AttestTyp === TEDLRFakturaAttestTYPEnum.BESLUTSATTEST
  );

  const numberOfUncertifiedReciverCertifiers =
    fakturaAttester.filter(
      (fa: any) =>
        fa.AttestTyp === TEDLRFakturaAttestTYPEnum.MOTTAGNINGSATTEST &&
        fa.STATUS === TEDLRFakturaAttestSTATUSEnum.UTDELAD
    )?.length ?? 0;

  if (!finnsBeslutsattestant && numberOfUncertifiedReciverCertifiers <= 1) {
    result.push({
      fieldName: '',
      message: 'Det saknas beslutsattestant på denna fakturadel',
      type: 'Error',
    });
  }
  return result;
};

const validateFleraAttestanter = (
  allaAttestanter: FakturaDelQuery_node_TEDLRFakturaDel__Associations_Faktura__Associations_Fakturadel__Associations_Fakturaattest[],
  attestTyp: TEDLRFakturaAttestAttestTypEnum,
  lrFleraAttestanterActive: boolean,
  minAttestantSign: string,
  myKodbeh: IAttestKodbehorighetUrval[],
  fakturaBelopp: number
): IFieldValidations => {
  const result: IFieldValidations = [];
  if (
    lrFleraAttestanterActive &&
    attestTyp === TEDLRFakturaAttestAttestTypEnum.BESLUTSATTEST
  ) {
    const hasValidKontrollAttestant = myKodbeh.some(kodbeh => {
      const kontrollbelopp = kodbeh?.kontrollbelopp ? kodbeh.kontrollbelopp : 0;

      return kodbeh.hasKontrollAttestant && fakturaBelopp > kontrollbelopp;
    });

    if (
      !containsOtherAttestant(minAttestantSign, allaAttestanter) &&
      !hasValidKontrollAttestant
    ) {
      result.push({
        fieldName: '',
        message: 'Minst två olika attestanter krävs',
        type: 'Error',
      });
    }
  }
  return result;
};

const validateCanControlCertify = (
  allaAttestanter: FakturaDelQuery_node_TEDLRFakturaDel__Associations_Faktura__Associations_Fakturadel__Associations_Fakturaattest[],
  attestTyp: TEDLRFakturaAttestAttestTypEnum
): IFieldValidations => {
  const result: IFieldValidations = [];

  const allCertifiersNotControl = allaAttestanter.filter(
    certifier =>
      certifier.AttestTyp !== TEDLRFakturaAttestAttestTypEnum.KONTROLLATTEST
  );

  if (
    !allCertifiersNotControl.every(
      certifier => certifier.STATUS === TEDLRFakturaAttestSTATUSEnum.ATTESTERAD
    ) &&
    attestTyp === TEDLRFakturaAttestAttestTypEnum.KONTROLLATTEST
  ) {
    result.push({
      fieldName: '',
      message:
        'Kontrollattest kan ske först när alla beslutsattestanter har attesterat',
      type: 'Error',
    });
  }
  return result;
};

export const useValidateFakturaDel: () => IValidateFn<FakturaDelQuery_node_TEDLRFakturaDel> =
  () => {
    const loginStateContext = useContext(LoginStateContext);
    const fakturaattestContext = useContext(FakturaattestWrapperContext);
    const konteringContext = useContext(KonteringContext);

    const lrFleraAttestanterActive: boolean = useMemo(() => {
      return (
        fakturaattestContext.sysOptions?.filter(
          (option: LRSysOptionsQuery_db_SYSOptions_All) =>
            option.OptionKey === 'LR_FLERA_ATTESTANTER'
        )?.[0].OptionData === 'Y'
      );
    }, [fakturaattestContext.sysOptions]);

    return ({ data, values }) => {
      const formData = createFormData(values, data) as FakturaDelType;
      const dataFormFakturaAttest = (formData?._Associations as any)
        ?.Fakturaattest;

      const attestTyp: TEDLRFakturaAttestAttestTypEnum =
        formData?.ATTEST_STATUS === 'BESLUTSATTESTERAS'
          ? formData?.AttestMinaStatus ===
            TEDLRFakturaDelAttestMinaStatusEnum.KONTROLLATTESTERAS
            ? TEDLRFakturaAttestAttestTypEnum.KONTROLLATTEST
            : TEDLRFakturaAttestAttestTypEnum.BESLUTSATTEST
          : TEDLRFakturaAttestAttestTypEnum.MOTTAGNINGSATTEST;

      const minFakturaAttest = getAktuellFakturaAttest(
        dataFormFakturaAttest,
        fakturaattestContext.reservForData,
        loginStateContext.sign,
        attestTyp
      );
      const konteringsForslagWithKodbeh =
        konteringContext.konteringUtils.getKonteringsForslagWithKodbeh();

      const myKodbeh: IAttestKodbehorighetUrval[] =
        konteringsForslagWithKodbeh.reduce(
          (prev: IAttestKodbehorighetUrval[], current) => {
            if (formData.id === current.fakturaID) {
              return prev.concat(current.attestkodbehorigheter);
            } else {
              return prev;
            }
          },
          []
        );

      const fakturaBelopp: number = Math.abs(formData?.FakturaBelopp2 ?? 0);

      const minAttestantSign =
        minFakturaAttest.aktuellFakturaAttest?.AttestantSign ?? '';

      const allaAttestanter: FakturaDelQuery_node_TEDLRFakturaDel__Associations_Faktura__Associations_Fakturadel__Associations_Fakturaattest[] =
        formData?._Associations?.Faktura?._Associations?.Fakturadel?.flatMap(
          curr => curr?._Associations?.Fakturaattest ?? []
        ) ?? [];

      const dataMergedWithChanges = mergeBusinessDataFormValues(
        parseText(formData),
        parseText(values)
      );

      let errors: IFieldValidations = [];

      errors = errors.concat(
        validateFinnsBAOnFakturaDel(
          dataMergedWithChanges?._Associations?.Fakturaattest ?? []
        )
      );

      errors = errors.concat(
        validateFleraAttestanter(
          allaAttestanter,
          attestTyp,
          lrFleraAttestanterActive,
          minAttestantSign,
          myKodbeh,
          fakturaBelopp
        )
      );

      errors = errors.concat(
        validateCanControlCertify(allaAttestanter, attestTyp)
      );

      return errors;
    };
  };
